import { authApi } from 'api';

// Helper function to enable/disable dry run mode
let dryRunMode = false; // Set to true for testing, set to false for production

export const setDryRunMode = (enabled) => {
  dryRunMode = enabled;
  console.log(`Volunteer API dry run mode ${enabled ? 'ENABLED' : 'DISABLED'}`);
};

export const isDryRunMode = () => dryRunMode;

export const getVolunteerRoles = async (clubId, includeHidden = false) => {
  return await authApi.get(
    `/Volunteers/roles?clubId=${clubId}&includeHidden=${includeHidden}`
  );
};

export const editVolunteerRole = async (clubId, volunteerRoleID, role) => {
  const body = {
    clubId,
    volunteerRoleID,
    ...role,
  };

  if (dryRunMode) {
    console.log('🔄 [DRY RUN] editVolunteerRole - Request that would be sent:');
    console.log('Endpoint: PUT /Volunteers/roles/edit');
    console.log('Request Body:', JSON.stringify(body, null, 2));
    return {
      data: {
        success: true,
        message: 'Dry run - no actual API call made',
        requestBody: body,
      },
    };
  }

  return await authApi.put(`/Volunteers/roles/edit`, body);
};

export const addVolunteerRole = async (clubId, role) => {
  const body = {
    clubId,
    ...role,
  };

  if (dryRunMode) {
    console.log('➕ [DRY RUN] addVolunteerRole - Request that would be sent:');
    console.log('Endpoint: POST /Volunteers/roles/create');
    console.log('Request Body:', JSON.stringify(body, null, 2));
    return {
      data: {
        success: true,
        message: 'Dry run - no actual API call made',
        requestBody: body,
      },
    };
  }

  return await authApi.post(`/Volunteers/roles/create`, body);
};

// Role deletion uses the confirm-and-cascade impact protocol
// (splitfast-portal docs/volunteer-role-deletion-design.md §4): a delete whose
// resolutions don't cover the role's open signups returns 409 + an impact
// report; resubmit with the report's cancelSignupIds to confirm.
export const deleteVolunteerRole = async (clubId, roleId, cancelSignupIds) => {
  return await authApi.delete(`/Volunteers/roles/${roleId}?clubId=${clubId}`, {
    data:
      cancelSignupIds && cancelSignupIds.length > 0
        ? { resolutions: { cancelSignupIds } }
        : {},
  });
};

export const getMeetVolunteerRequirements = async (meetId) => {
  return await authApi.get(`/Volunteers/meet-volunteer-requirements/${meetId}`);
};

// Removing requirements with open signups follows the confirm-and-cascade
// protocol (volunteer-role-deletion-design.md §16): without covering
// resolutions the PUT returns 409 + a report naming the signups; resubmit
// with the report's cancelSignupIds to confirm.
export const updateMeetVolunteerRequirements = async (
  meetId,
  requirements,
  cancelSignupIds
) => {
  const requestBody = { requirements };
  if (cancelSignupIds && cancelSignupIds.length > 0) {
    requestBody.resolutions = { cancelSignupIds };
  }

  if (dryRunMode) {
    console.log(
      '📋 [DRY RUN] updateMeetVolunteerRequirements - Request that would be sent:'
    );
    console.log(
      `Endpoint: PUT /Volunteers/meet-volunteer-requirements/${meetId}`
    );
    console.log('Request Body:', JSON.stringify(requestBody, null, 2));

    return {
      data: {
        success: true,
        message: 'Dry run - no actual API call made',
        meetId,
        requestBody,
      },
    };
  }

  return await authApi.put(
    `/Volunteers/meet-volunteer-requirements/${meetId}`,
    requestBody
  );
};

export const signupForVolunteerRole = async ({
  meetVolunteerRequirementID,
  notes,
}) => {
  const body = {
    meetVolunteerRequirementID,
    notes,
  };

  if (dryRunMode) {
    console.log(
      '✍️ [DRY RUN] signupForVolunteerRole - Request that would be sent:'
    );
    console.log('Endpoint: POST /Volunteers/signup');
    console.log('Request Body:', JSON.stringify(body, null, 2));
    return {
      data: {
        success: true,
        message: 'Dry run - no actual API call made',
        requestBody: body,
      },
    };
  }

  return await authApi.post('/Volunteers/signup', body);
};

export const getAllMeetSignups = async (meetId) => {
  return await authApi.get(`/Volunteers/all-meet-signups/${meetId}`);
};

// Cancellation goes through the manage-signups bulk API; members may only
// 'delete' their own 'Registered' signups. That endpoint returns 200 with
// per-action results, so surface an action failure as a thrown error.
export const deleteVolunteerSignup = async (signupId) => {
  const response = await manageSignups({
    actions: [{ signupId, action: 'delete' }],
  });

  const result = response.data?.results?.[0];
  if (result && !result.success) {
    throw new Error(result.error || 'Failed to cancel volunteer signup');
  }

  return response;
};

export const getVolunteerDashboard = async (clubId, userId = null) => {
  const params = { clubId };
  if (userId) {
    params.userId = userId;
  }
  return await authApi.get('/Volunteers/dashboard', { params });
};

export const getVolunteerOpportunities = async (clubId, userId = null) => {
  const params = { clubId };
  if (userId) {
    params.userId = userId;
  }
  return await authApi.get(`/Volunteers/opportunities/${clubId}`, { params });
};

export const getVolunteerOpportunitiesSummary = async (
  clubId,
  userId = null
) => {
  const params = {};
  if (userId) {
    params.userId = userId;
  }
  return await authApi.get(`/Volunteers/opportunities/summary/${clubId}`, {
    params,
  });
};

export const manageSignups = async (data) => {
  if (dryRunMode) {
    console.log('👥 [DRY RUN] manageSignups - Request that would be sent:');
    console.log('Endpoint: POST /Volunteers/manage-signups');
    console.log('Request Body:', JSON.stringify(data, null, 2));
    return {
      data: {
        success: true,
        message: 'Dry run - no actual API call made',
        requestBody: data,
      },
    };
  }

  return await authApi.post('/Volunteers/manage-signups', data);
};

// Get all active club members
export const getClubMembers = async (clubId) => {
  return await authApi.get(`/Users/${clubId}/members?activeOnly=true`);
};

// Bulk signup members for a role
export const bulkSignup = async (data) => {
  if (dryRunMode) {
    console.log('👥 [DRY RUN] bulkSignup - Request that would be sent:');
    console.log('Endpoint: POST /Volunteers/bulk-signup');
    console.log('Request Body:', JSON.stringify(data, null, 2));
    return {
      data: {
        success: true,
        message: 'Dry run - no actual API call made',
        requestBody: data,
      },
    };
  }

  return await authApi.post('/Volunteers/bulk-signup', data);
};

// Get user's upcoming signups
export const getUserUpcomingSignups = async (userId, clubId) => {
  return await authApi.get(
    `/Volunteers/user/${userId}/upcoming?clubId=${clubId}`
  );
};
